/* global fbq */

import React, { useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import defaultOG from "../assets/og/home.png";

// Load all posts at build
function importAllPosts(r) {
  return r.keys().map((key) => ({
    ...r(key),
    slug: key.replace(/^\.\/|\.json$/g, ""),
  }));
}
const posts = importAllPosts(
  require.context("../blog-posts", false, /\.json$/)
);

// Bundle all images from src/assets/Blog
function importBlogImages(r) {
  return r.keys().reduce((images, path) => {
    const fileName = path.replace(/^\.\//, "");
    images[fileName] = r(path).default || r(path);
    return images;
  }, {});
}
const blogImages = importBlogImages(
  require.context("../assets/Blog", false, /\.(png|jpe?g|svg)$/)
);

export default function BlogPost() {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);
  const articleRef = useRef(null);

  const pushToDataLayer = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-white bg-[var(--bg-page)]">
        <h1 className="text-2xl font-bold mb-4">404 - Blog Post Not Found</h1>
        <Link to="/blog" className="text-[var(--blue)]">
          ← Back to Blog
        </Link>
      </div>
    );
  }

  const heroImgRel = blogImages[post.imageHero];
  const ogImgRel = blogImages[post.image] || defaultOG;
  const ogImgAbs = `${window.location.origin}${ogImgRel}`;

  return (
    <>
      <Helmet>
        <title>{post.title} | EloFactory Blog</title>
        <meta name="description" content={post.summary} />
        <meta name="keywords" content={post.metaKeywords} />
        <link rel="canonical" href={post.canonical} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="EloFactory" />
        <meta property="og:url" content={post.canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.summary} />
        <meta property="og:image" content={ogImgAbs} />

        {/* Article meta */}
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.date} />
        <meta property="article:author" content={post.author?.name} />
        {post.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@elofactorygg" />
        <meta name="twitter:creator" content="@elofactorygg" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.summary} />
        <meta name="twitter:image" content={ogImgAbs} />
        <meta
          name="twitter:image:alt"
          content={`Cover image for ${post.title}`}
        />

        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            image: [ogImgAbs],
            datePublished: post.date,
            dateModified: post.date,
            author: { "@type": "Person", name: post.author?.name },
            publisher: { "@type": "Organization", name: "EloFactory Team" },
            description: post.summary,
          })}
        </script>
      </Helmet>

      <main className="min-h-screen bg-[var(--bg-page)] pt-16 px-2 md:px-0">
        {/* Hero */}
        <section className="max-w-6xl mx-auto rounded-md overflow-hidden relative mb-10 shadow-lg">
          <img
            src={heroImgRel}
            alt={post.imageAlt || post.title}
            className="absolute inset-0 w-full h-full object-cover opacity-50"
            style={{ zIndex: 1 }}
          />
          <div className="relative z-10 p-8 md:p-12">
            <Link to="/blog" className="text-[var(--blue)] mb-4 inline-block">
              ← Back to Blog
            </Link>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 drop-shadow">
              {post.title}
            </h1>
            <div className="flex flex-wrap gap-2 mb-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-[var(--border)] rounded-md px-2 py-1 text-[var(--secondary-text)]"
                >
                  {tag}
                </span>
              ))}
            </div>
            <time className="block text-xs text-[var(--secondary-text)] mb-4">
              {new Date(post.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </time>
            <p className="text-lg md:text-xl text-white max-w-3xl">
              {post.summary}
            </p>
          </div>
        </section>

        {/* Author Box */}
        {post.author && (
          <div className="max-w-6xl mx-auto flex items-center gap-4 bg-[var(--bg-surface-variant)] rounded-md px-5 py-4 mb-8 border border-[var(--border)]">
            <img
              src={require(`../assets/Logos/${post.author.avatar}`)}
              alt={post.author.name}
              className="w-20"
            />
            <div>
              <div className="font-semibold text-white">
                {post.author.name}
              </div>
              <div className="text-xs text-[var(--secondary-text)]">
                {post.author.bio}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <article
          ref={articleRef}
          className="max-w-6xl mx-auto bg-[var(--bg-surface)] border border-[var(--border)] rounded-md p-6 md:p-10 shadow mb-10"
        >
          {post.sections.map((sec, i) => (
            <section key={i} className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                {sec.heading}
              </h2>
              {/* Markdown wrapper */}
              <div className="prose prose-invert text-[var(--secondary-text)] space-y-4">
                <ReactMarkdown
                  components={{
                    img: ({ node, ...props }) => {
                      const src = blogImages[props.src] || props.src;
                      return <img {...props} src={src} alt={props.alt || ""} />;
                    },
                  }}
                >
                  {sec.body}
                </ReactMarkdown>
              </div>
            </section>
          ))}

          {/* Pro Tips */}
          {post.proTips && (
            <aside className="bg-[var(--bg-surface-variant)] border-l-2 border-[var(--blue)] rounded-md p-5 mb-10">
              <div className="font-bold text-[var(--blue)] mb-2">Pro Tips</div>
              <ul className="list-disc list-inside space-y-1 text-[var(--secondary-text)]">
                {post.proTips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </aside>
          )}

          {/* FAQ */}
          {post.faq && (
            <section className="mb-10">
              <h3 className="text-xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h3>
              <dl className="space-y-4">
                {post.faq.map((f) => (
                  <div key={f.q}>
                    <dt className="font-semibold text-[var(--blue)]">{f.q}</dt>
                    <dd className="text-[var(--secondary-text)]">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* Call to Action */}
          {post.callToAction && (
            <aside className="mt-10 pt-6 text-center border-t border-[var(--border)]">
              <div className="mb-2 text-lg text-white font-bold">
                {post.callToAction.text}
              </div>
              <Link
                to={post.callToAction.button.url}
                onClick={() => {
                  if (typeof fbq !== "undefined") {
                    fbq("track", "Lead", { content_name: post.title });
                  }
                  pushToDataLayer({
                    event: "blog_cta_click",
                    blog_title: post.title,
                    cta_label: post.callToAction.button.label,
                  });
                }}
                className="inline-block px-6 py-3 mt-2 bg-[var(--blue)] text-white font-bold rounded-md shadow"
              >
                {post.callToAction.button.label}
              </Link>
            </aside>
          )}
        </article>
      </main>
    </>
  );
}
